import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getCountdown } from "../helper/Countdown";
import toast from "react-hot-toast";
import productData from "../data/brother-products.json";

const DealsOne = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);

  // Load JSON from import instead of fetch
  useEffect(() => {
    setProducts(productData);
  }, []);

  // Countdown (kept)
  const [timeLeft, setTimeLeft] = useState(getCountdown());
  useEffect(() => {
    const interval = setInterval(() => setTimeLeft(getCountdown()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Add to cart logic
  const handleAddToCart = (product) => {
    const isLoggedIn = sessionStorage.getItem("isLoggedIn");

    if (isLoggedIn !== "true") {
      toast.error("Please login first!");
      navigate("/login-now");
      return;
    }

    let cart = JSON.parse(sessionStorage.getItem("cart")) || [];
    const existing = cart.find((item) => item.id === product.id);

    if (existing) {
      existing.quantity++;
    } else {
      cart.push({ ...product, quantity: 1 });
    }

    sessionStorage.setItem("cart", JSON.stringify(cart));

    // 🔥 Trigger header update instantly (fixes no-refresh issue)
    window.dispatchEvent(new Event("cart-updated"));

    toast.success(`${product.name} added to cart!`);
  };

  const formatCategory = (str) =>
    str.replace(/[-_]/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  // Generate extra UI metadata
  const productsWithRandomData = useMemo(() => {
    return products.map((item) => {
      const rating = (Math.random() * 1 + 4).toFixed(1);
      const reviews = Math.floor(Math.random() * 400) + 100;
      const randomTags = ["Wi-Fi", "Best Seller", "Smart Print", "Eco Mode"]
        .sort(() => 0.5 - Math.random())
        .slice(0, 2);

      return {
        ...item,
        rating,
        reviews,
        features: randomTags,
        formattedCategory: formatCategory(item.category || "Printer"),
      };
    });
  }, [products]);

  return (
    <section className="deals pt-30 pb-30">
      <div className="container container-lg">
        {/* ---- NEW HEADING BLOCK ---- */}
        <div className="title-area text-center">
          <h2 className="fresh-heading">Top Deals on Popular Printers</h2>
          <p className="fresh-sub">
            Discover high-performance printers chosen by customers — powerful,
            reliable, and perfect for everyday use.
          </p>
        </div>

        {/* ---- PRODUCT GRID ---- */}
        <div className="product-grid">
          {productsWithRandomData.map((item) => {
            const img =
              item.image || item.imageUrl || "/assets/images/placeholder.png";
            const finalPrice = item.salePrice || item.price;

            return (
              <div className="prod-card" key={item.id}>
                {/* IMAGE */}
                <Link
                  to={`/product-details/${item.id}`}
                  className="prod-img-wrap"
                >
                  <img src={img} alt={item.name} />
                  {item.salePrice && <span className="prod-badge">Sale</span>}
                </Link>

                {/* CONTENT */}
                <div className="prod-body">
                  <p className="prod-category">{item.formattedCategory}</p>

                  <Link
                    to={`/product-details/${item.id}`}
                    className="prod-title"
                  >
                    {item.name}
                  </Link>

                  <div className="prod-features">
                    {item.features.map((tag, i) => (
                      <span key={i} className="prod-chip">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="prod-pricing">
                    <span className="new-price">${finalPrice}</span>
                    {item.salePrice && (
                      <span className="old-price">${item.price}</span>
                    )}
                  </div>

                  <div className="prod-rating">
                    ⭐ {item.rating} <span>({item.reviews} reviews)</span>
                  </div>

                  <button
                    className="prod-btn"
                    onClick={() => handleAddToCart(item)}
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ---------- NEW PREMIUM STYLES ---------- */}
      <style>{`
    /* Heading */
    .fresh-heading {
      font-size: 34px;
      font-weight: 800;
      margin-bottom: 10px;
      color: #0f172a;
    }

    .fresh-sub {
      font-size: 16px;
      color: #64748b;
      max-width: 600px;
      margin: 0 auto 40px auto;
    }

    /* Product Grid */
    .product-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
      gap: 32px;
    }

    /* Card */
    .prod-card {
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      overflow: hidden;
      transition: .25s;
    }

    .prod-card:hover {
      transform: translateY(-6px);
      border-color: #4c8bf5;
      box-shadow: 0px 10px 25px rgba(0,0,0,0.08);
    }

    /* Image Block */
    .prod-img-wrap {
      background: #f8fafc;
      padding: 26px;
      display: flex;
      justify-content: center;
      position: relative;
    }

    .prod-img-wrap img {
      width: 80%;
      transition: .3s;
    }

    .prod-card:hover img {
      transform: scale(1.08);
    }

    .prod-badge {
      position: absolute;
      top: 12px;
      left: 12px;
      background: #ff3b30;
      color: #fff;
      padding: 6px 12px;
      font-size: 12px;
      border-radius: 4px;
      text-transform: uppercase;
      font-weight: 700;
    }

    /* Content */
    .prod-body {
      padding: 20px;
    }

    .prod-category {
      font-size: 13px;
      color: #64748b;
      font-weight: 600;
      margin-bottom: 6px;
    }

    .prod-title {
      display: block;
      font-size: 18px;
      font-weight: 700;
      color: #1e293b;
      text-decoration: none;
      margin-bottom: 12px;
      transition: .2s;
    }

    .prod-title:hover {
      color: #4c8bf5;
    }

    .prod-features {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 12px;
    }

    .prod-chip {
      background: #eef6ff;
      padding: 5px 10px;
      border-radius: 6px;
      font-size: 12px;
      color: #0072f5;
      font-weight: 600;
    }

    /* Pricing */
    .prod-pricing {
      display: flex;
      gap: 10px;
      align-items: center;
      margin-bottom: 8px;
    }

    .new-price {
      font-size: 22px;
      font-weight: 800;
      color: #0b5ed7;
    }

    .old-price {
      text-decoration: line-through;
      font-size: 14px;
      color: #94a3b8;
    }

    /* Rating */
    .prod-rating {
      font-size: 14px;
      color: #ff9f00;
      font-weight: 600;
      margin-bottom: 14px;
    }

    .prod-rating span {
      color: #475569;
      font-weight: 500;
    }

    /* Button */
    .prod-btn {
      width: 100%;
      background: #4c8bf5;
      color: #fff;
      border: none;
      padding: 12px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 15px;
      font-weight: 700;
      transition: .25s;
    }

    .prod-btn:hover {
      background: #2364d8;
    }
  `}</style>
    </section>
  );
};

export default DealsOne;
